import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Space,
    message,
    Popconfirm,
    Tag,
    Divider,
    Card,
    Row,
    Col
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CloseOutlined,
    SaveOutlined,
    DownloadOutlined,
    UploadOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const ScenariosPage = () => {
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingScenario, setEditingScenario] = useState(null);
    const [form] = Form.useForm();
    const [fields, setFields] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        fetchScenarios();
    }, [pagination.current, pagination.pageSize, searchText]);

    const fetchScenarios = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/scenarios', {
                params: {
                    page: pagination.current,
                    limit: pagination.pageSize,
                    search: searchText
                }
            });
            setScenarios(response.data.data);
            setPagination({
                ...pagination,
                total: response.data.total
            });
        } catch (error) {
            message.error('Ошибка при загрузке сценариев');
        } finally {
            setLoading(false);
        }
    };

    const handleAddField = () => {
        setFields([...fields, {
            field_type: 'main',
            system_name: '',
            human_name: '',
            field_id: '',
            possible_values: []
        }]);
    };

    const handleRemoveField = (index) => {
        const newFields = [...fields];
        newFields.splice(index, 1);
        setFields(newFields);
    };

    const handleFieldChange = (index, field, value) => {
        const newFields = [...fields];
        newFields[index][field] = value;
        setFields(newFields);
    };

    const handleAddValue = (fieldIndex) => {
        const newFields = [...fields];
        newFields[fieldIndex].possible_values = [...newFields[fieldIndex].possible_values, ''];
        setFields(newFields);
    };

    const handleRemoveValue = (fieldIndex, valueIndex) => {
        const newFields = [...fields];
        newFields[fieldIndex].possible_values.splice(valueIndex, 1);
        setFields(newFields);
    };

    const handleValueChange = (fieldIndex, valueIndex, value) => {
        const newFields = [...fields];
        newFields[fieldIndex].possible_values[valueIndex] = value;
        setFields(newFields);
    };

    // *** НАЧАЛО: НОВЫЙ КОД ДЛЯ ЭКСПОРТА И ИМПОРТА ***

    const handleExport = async () => {
        setLoading(true);
        try {
            // Получаем все записи для полного экспорта
            const response = await axios.get('/api/scenarios', { params: { limit: 10000 } });
            const scenariosToExport = response.data.data;

            const dataToSheet = [];
            scenariosToExport.forEach(scenario => {
                if (scenario.fields && scenario.fields.length > 0) {
                    scenario.fields.forEach(field => {
                        dataToSheet.push({
                            'ID Сценария': scenario.id,
                            'Название Сценария': scenario.scenario_name,
                            'Удалить Сценарий': 'Нет',
                            'Тип поля': field.field_type,
                            'Системное название': field.system_name,
                            'Человеческое название': field.human_name,
                            'ID поля': field.field_id,
                            'Возможные значения': field.possible_values ? field.possible_values.join(', ') : ''
                        });
                    });
                } else {
                    // Если у сценария нет полей, все равно добавляем его, чтобы можно было удалить
                    dataToSheet.push({
                        'ID Сценария': scenario.id,
                        'Название Сценария': scenario.scenario_name,
                        'Удалить Сценарий': 'Нет',
                    });
                }
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(dataToSheet);
            XLSX.utils.book_append_sheet(wb, ws, "Сценарии");
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, 'сценарии.xlsx');
            message.success('Экспорт успешно завершен');
        } catch (error) {
            message.error('Ошибка при экспорте данных');
            console.error('Export error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!window.confirm('Вы уверены? Существующие сценарии будут обновлены, а помеченные - удалены.')) {
            e.target.value = '';
            return;
        }

        setLoading(true);
        try {
            const data = await readExcelFile(file);
            const response = await axios.post('/api/scenarios/import', data, {});
            message.success(response.data.message || 'Импорт успешно завершен!');
            fetchScenarios(); // Обновляем данные в таблице
        } catch (error) {
            message.error(error.response?.data?.message || 'Ошибка при импорте данных');
            console.error('Import error:', error);
        } finally {
            e.target.value = ''; // Сбрасываем инпут
            setLoading(false);
        }
    };

    const readExcelFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet);

                    // Собираем плоские данные в иерархическую структуру
                    const scenariosMap = {};
                    jsonData.forEach(row => {
                        const scenarioId = row['ID Сценария'];
                        if (!scenarioId) return; // Пропускаем пустые строки

                        if (!scenariosMap[scenarioId]) {
                            scenariosMap[scenarioId] = {
                                id: scenarioId,
                                scenario_name: row['Название Сценария'],
                                delete_record: row['Удалить Сценарий'] === 'Да' || row['Удалить Сценарий'] === 1,
                                fields: []
                            };
                        }

                        // Добавляем поле, только если оно есть в строке
                        if(row['Системное название']) {
                            scenariosMap[scenarioId].fields.push({
                                field_type: row['Тип поля'],
                                system_name: row['Системное название'],
                                human_name: row['Человеческое название'],
                                field_id: row['ID поля'],
                                possible_values: String(row['Возможные значения'] || '').split(',').map(v => v.trim()).filter(Boolean)
                            });
                        }
                    });

                    resolve(Object.values(scenariosMap));
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsArrayBuffer(file);
        });
    };

    const showModal = (scenario = null) => {
        if (scenario) {
            setEditingScenario(scenario);
            form.setFieldsValue({
                scenario_name: scenario.scenario_name
            });
            setFields(scenario.fields.map(field => ({
                ...field,
                possible_values: field.possible_values || []
            })));
        } else {
            setEditingScenario(null);
            form.resetFields();
            setFields([{
                field_type: 'main',
                system_name: '',
                human_name: '',
                field_id: '',
                possible_values: []
            }]);
        }
        setIsModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const data = {
                scenario_name: values.scenario_name,
                fields: fields.map(field => ({
                    ...field,
                    possible_values: field.possible_values.filter(v => v.trim() !== '')
                })).filter(field =>
                    field.system_name.trim() !== '' &&
                    field.human_name.trim() !== ''
                )
            };

            if (data.fields.length === 0) {
                message.error('Должно быть хотя бы одно поле');
                return;
            }

            if (editingScenario) {
                await axios.put(`/api/scenarios/${editingScenario.id}`, data);
                message.success('Сценарий успешно обновлен');
            } else {
                await axios.post('/api/scenarios', data);
                message.success('Сценарий успешно создан');
            }

            setIsModalVisible(false);
            fetchScenarios();
        } catch (error) {
            message.error(error.response?.data?.message || 'Ошибка при сохранении сценария');
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/api/scenarios/${id}`);
            message.success('Сценарий успешно удален');
            fetchScenarios();
        } catch (error) {
            message.error('Ошибка при удалении сценария');
        }
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            sorter: true,
        },
        {
            title: 'Название сценария',
            dataIndex: 'scenario_name',
            key: 'scenario_name',
            sorter: true,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Поля',
            key: 'fields',
            render: (_, record) => (
                <div>
                    {record.fields.map((field, i) => (
                        <Card
                            key={i}
                            size="small"
                            title={`${field.human_name} (${field.system_name})`}
                            style={{ marginBottom: 16 }}
                            extra={<Tag color={field.field_type === 'main' ? 'blue' : 'orange'}>
                                {field.field_type === 'main' ? 'Основной' : 'Кастомный'}
                            </Tag>}
                        >
                            <Row gutter={16}>
                                {field.field_id && (
                                    <Col span={24}>
                                        <strong>ID поля:</strong> {field.field_id}
                                    </Col>
                                )}
                                <Col span={24}>
                                    <strong>Возможные значения:</strong>
                                    <div style={{ marginTop: 8 }}>
                                        {field.possible_values?.length > 0 ? (
                                            field.possible_values.map((value, idx) => (
                                                <Tag key={idx} style={{ marginBottom: 4 }}>{value}</Tag>
                                            ))
                                        ) : <Tag>Нет значений</Tag>}
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    ))}
                </div>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => showModal(record)}
                    />
                    <Popconfirm
                        title="Удалить сценарий?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Layout>
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h2>Управление сценариями</h2>
                <div>
                    <Input.Search
                        placeholder="Поиск сценариев"
                        allowClear
                        enterButton
                        style={{ width: 300, marginRight: 16 }}
                        onSearch={(value) => {
                            setSearchText(value);
                            setPagination({ ...pagination, current: 1 });
                        }}
                    />

                    { /* *** НАЧАЛО: НОВЫЕ КНОПКИ *** */ }
                    <Button icon={<DownloadOutlined />} onClick={handleExport} style={{ marginRight: 8 }}>
                        Экспорт
                    </Button>
                    <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current.click()}>
                        Импорт
                    </Button>
                    <input type="file" ref={fileInputRef} onChange={handleImport} accept=".xlsx, .xls" style={{ display: 'none' }} />
                    { /* *** КОНЕЦ: НОВЫЕ КНОПКИ *** */ }

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal()}
                    >
                        Добавить
                    </Button>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={scenarios}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                bordered
            />

            <Modal
                title={editingScenario ? 'Редактировать сценарий' : 'Добавить новый сценарий'}
                visible={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
                width={800}
                footer={[
                    <Button key="back" onClick={() => setIsModalVisible(false)} icon={<CloseOutlined />}>
                        Отмена
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleSubmit}
                        icon={<SaveOutlined />}
                    >
                        Сохранить
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="scenario_name"
                        label="Название сценария"
                        rules={[{ required: true, message: 'Введите название сценария' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Divider orientation="left">Поля сценария</Divider>

                    {fields.map((field, index) => (
                        <Card
                            key={index}
                            size="small"
                            style={{ marginBottom: 16 }}
                            extra={
                                <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemoveField(index)}
                                />
                            }
                        >
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item label="Тип поля">
                                        <Select
                                            value={field.field_type}
                                            onChange={(value) => handleFieldChange(index, 'field_type', value)}
                                        >
                                            <Option value="main">Основной</Option>
                                            <Option value="custom">Кастомный</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Системное название">
                                        <Input
                                            value={field.system_name}
                                            onChange={(e) => handleFieldChange(index, 'system_name', e.target.value)}
                                            placeholder="system_field_name"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Человеческое название">
                                        <Input
                                            value={field.human_name}
                                            onChange={(e) => handleFieldChange(index, 'human_name', e.target.value)}
                                            placeholder="Название поля"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="ID поля (необязательно)">
                                        <Input
                                            value={field.field_id}
                                            onChange={(e) => handleFieldChange(index, 'field_id', e.target.value)}
                                            placeholder="Идентификатор поля"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="Возможные значения">
                                        {field.possible_values?.map((value, valueIndex) => (
                                            <div key={valueIndex} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Input
                                                    value={value}
                                                    onChange={(e) => handleValueChange(index, valueIndex, e.target.value)}
                                                    placeholder="Значение"
                                                />
                                                <Button
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleRemoveValue(index, valueIndex)}
                                                />
                                            </div>
                                        ))}
                                        <Button
                                            type="dashed"
                                            icon={<PlusOutlined />}
                                            onClick={() => handleAddValue(index)}
                                            block
                                        >
                                            Добавить значение
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    ))}

                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddField}
                        block
                    >
                        Добавить поле
                    </Button>
                </Form>
            </Modal>
        </div>
        </Layout>
    );
};

export default ScenariosPage;